/**
 * Accountless identity + entitlement state for DateWise Wingman.
 *
 * - Generates a UUID-v4 once per device, persists in localStorage.
 * - Polls /api/access/status to keep tier + reads_remaining in sync.
 * - Exposes a refresh() the rest of the app can call after analyze, after
 *   checkout success, or after restore.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const TOKEN_KEY = "wingman_token";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function uuid4() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getOrCreateToken() {
  if (typeof window === "undefined") return "";
  let t = window.localStorage.getItem(TOKEN_KEY);
  if (!t) {
    t = uuid4();
    window.localStorage.setItem(TOKEN_KEY, t);
  }
  return t;
}

export function resetToken() {
  if (typeof window === "undefined") return "";
  const fresh = uuid4();
  window.localStorage.setItem(TOKEN_KEY, fresh);
  return fresh;
}

/** Authed axios — auto-injects X-Wingman-Token + X-Analytics-Session. */
export function authedAxios() {
  let analyticsSession = "";
  try {
    if (typeof window !== "undefined") {
      analyticsSession = window.localStorage.getItem("wingman_analytics_session") || "";
    }
  } catch {
    // ignore
  }
  return axios.create({
    baseURL: API,
    headers: {
      "X-Wingman-Token": getOrCreateToken(),
      ...(analyticsSession ? { "X-Analytics-Session": analyticsSession } : {}),
    },
  });
}

const FREE = {
  tier: "free",
  reads_remaining: 0,
  status: "free",
  period_end: null,
  expires_at: null,
  locked: true,
};

export function useAccess() {
  const [access, setAccess] = useState(FREE);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await authedAxios().get("/access/status");
      setAccess(data || FREE);
    } catch {
      setAccess(FREE);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { access, loading, refresh };
}

export { API };
